import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook to fetch field suggestions for autocomplete
 * @param {string} entityName - Entity name (e.g., 'customer', 'lead')
 * @param {string} fieldName - Field name to get suggestions for
 * @param {string|null} columnKey - Optional column key for table type fields
 * @param {boolean} enabled - Whether to fetch suggestions (default: true)
 * @returns {Object} { suggestions: string[], loading: boolean, error: Error|null }
 */
export const useFieldSuggestions = (entityName, fieldName, columnKey = null, enabled = true) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Cache for suggestions per field/column combination
  const cacheRef = useRef(new Map());
  const debounceTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  const cacheKey = `${entityName}:${fieldName}:${columnKey || 'default'}`;

  const fetchSuggestions = useCallback(async () => {
    if (!enabled || !entityName || !fieldName) {
      return;
    }

    // Check cache first
    if (cacheRef.current.has(cacheKey)) {
      const cached = cacheRef.current.get(cacheKey);
      // Use cached data if less than 5 minutes old
      if (Date.now() - cached.timestamp < 5 * 60 * 1000) {
        setSuggestions(cached.suggestions);
        return;
      }
    }

    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        limit: '50'
      });
      
      if (columnKey) {
        params.append('column', columnKey);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(
        `/api/tenant-entity-config/${entityName}/field-suggestions/${fieldName}?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          signal: abortControllerRef.current.signal
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch suggestions: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success && Array.isArray(data.suggestions)) {
        // Update cache
        cacheRef.current.set(cacheKey, {
          suggestions: data.suggestions,
          timestamp: Date.now()
        });
        
        setSuggestions(data.suggestions);
      } else {
        setSuggestions([]);
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        // Request was cancelled, ignore
        return;
      }
      console.error('Error fetching field suggestions:', err);
      setError(err);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [entityName, fieldName, columnKey, enabled, cacheKey]);

  // Debounced fetch
  useEffect(() => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (!enabled || !entityName || !fieldName) {
      setSuggestions([]);
      return;
    }

    // Debounce the fetch by 300ms
    debounceTimeoutRef.current = setTimeout(() => {
      fetchSuggestions();
    }, 300);

    // Cleanup
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchSuggestions, enabled, entityName, fieldName]);

  // Clear suggestions when disabled
  useEffect(() => {
    if (!enabled) {
      setSuggestions([]);
      setLoading(false);
      setError(null);
    }
  }, [enabled]);

  return {
    suggestions,
    loading,
    error
  };
};

export default useFieldSuggestions;

